// Synth / city pop instruments (docs/AUDIO_DESIGN.md §12), used only by synth.js: a Juno-style poly synth on a BBD-like
// stereo chorus (replaces the EP), a resonant synth bass, a square or FM glass lead with the dotted-8th delay, and the
// kit plus a tom that tunes itself down a fill. The pad allocates 2 saws + LP + amp per note (cap with stealing); bass,
// lead and tom are persistent and gated with setTargetAtTime (a note-on cancels what the last note left scheduled).
import { clamp } from '../../engine/core.js';
import { mtof, hold } from '../dsp.js';
import { LEVELS, createKit, gain, filter, osc, panner, persistent as persist } from '../instruments.js';

// Level knobs (the harness zeroes them to measure one part alone).
export const SYNTH_LEVELS = { pad: 1, bass: 1, lead: 1, tom: 1 };

const delay = (ac, t) => { const d = ac.createDelay(0.05); d.delayTime.value = t; return d; };
const lerp = (a, b, x) => a + (b - a) * x;

/* ---------------- poly synth: 2 detuned saws → LP with a filter envelope → amp; bus LP → stereo chorus ---------------- */
/** p = plan.synth: { detune (± cents), cutoff (sustain Hz at C4), env, res, atk, rel, chorus: { rate, depth } }; lp = bus LP.
 *  Articulation follows the length: a 16th stab (≤ 0.15 s) gets a 1.5 ms attack and a filter spike decaying τ 80 ms;
 *  a pad (≥ 0.4 s) swells in (τ p.atk) with the filter opening over ~0.3 s and sustains at 0.8. */
export function createPoly(ac, out, send, p, lp, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null }) {
  // BBD-style chorus: dry .72 centre + two delay lines (6.5 / 9.5 ms) swept in antiphase by one triangle LFO, panned ∓.9
  const busIn = gain(ac, 1), busLp = filter(ac, 'lowpass', lp, 0.5), dry = gain(ac, 0.72), sendG = gain(ac, 0.25);
  const dL = delay(ac, 0.0065), dR = delay(ac, 0.0095), wL = gain(ac, 0.5), wR = gain(ac, 0.5), pL = panner(ac, -0.9), pR = panner(ac, 0.9);
  const lfo = osc(ac, 'triangle', p.chorus.rate), lfoL = gain(ac, p.chorus.depth), lfoR = gain(ac, -p.chorus.depth);
  busIn.connect(busLp); busLp.connect(dry); dry.connect(out); busLp.connect(sendG); sendG.connect(send);
  busLp.connect(dL); dL.connect(wL); wL.connect(pL); pL.connect(out);
  busLp.connect(dR); dR.connect(wR); wR.connect(pR); pR.connect(out);
  lfo.connect(lfoL); lfoL.connect(dL.delayTime); lfo.connect(lfoR); lfoR.connect(dR.delayTime);
  const bus = persist(registry, tag, [lfo], [busIn, busLp, dry, sendG, dL, dR, wL, wR, pL, pR, lfo, lfoL, lfoR], t0);
  let voices = [], maxActive = 0, steals = 0, notes = 0;           // { v, midi, end (sounding end), rec }
  const cut = (x, t) => { registry.stop(x.v, t, 0.01); x.end = t; if (x.rec) x.rec[1] = t; };

  function note(t, midi, vel, dur, relTau = 0.08) {
    voices = voices.filter(x => x.end > t);
    const k = voices.findIndex(x => x.midi === midi);
    if (k >= 0) cut(voices.splice(k, 1)[0], t);                    // the key is struck again: its old voice lets go
    else if (voices.length >= cap) { cut(voices.shift(), t); steals++; }   // else steal the oldest (10 ms)
    const f = mtof(midi) * Math.pow(2, (r() * 4 - 2) / 1200);      // ±2 ¢ analog drift per note
    const s = clamp((0.4 - dur) / 0.25, 0, 1);                     // 1 = stab, 0 = pad
    const o1 = osc(ac, 'sawtooth', f), o2 = osc(ac, 'sawtooth', f), lpf = filter(ac, 'lowpass', 400, p.res), amp = gain(ac, 0);
    o1.detune.value = -p.detune; o2.detune.value = p.detune;
    o1.connect(lpf); o2.connect(lpf); lpf.connect(amp); amp.connect(busIn);
    // filter: sustain cutoff key-tracked (f/262)^0.5; peak = sustain × (1 + env·(.3 + .7v)·(.4 … 1 stab)), ≤ 6.5 kHz
    const fs = p.cutoff * clamp(Math.sqrt(f / 262), 0.7, 1.8), fp = Math.min(6500, fs * (1 + p.env * (0.3 + 0.7 * vel) * lerp(0.4, 1, s)));
    lpf.frequency.setValueAtTime(fs * 0.7, t);
    lpf.frequency.setTargetAtTime(fp, t, lerp(0.1, 0.002, s));
    lpf.frequency.setTargetAtTime(fs, t + lerp(0.3, 0.008, s), lerp(0.9, 0.08, s));
    // amp: peak 0.245·v^1.3 (stabs ×1.4), attack τ p.atk → 1.5 ms, down to .8 → .5 of peak (τ 1.2 → .15 s), release ≥ p.rel → 50 ms
    const A = 0.245 * lerp(1, 1.4, s) * Math.pow(vel, 1.3) * SYNTH_LEVELS.pad, off = t + Math.max(0.05, dur), rel = Math.max(relTau, lerp(p.rel, 0.05, s));
    amp.gain.setValueAtTime(0, t);
    amp.gain.setTargetAtTime(A, t, lerp(p.atk, 0.0015, s));
    amp.gain.setTargetAtTime(A * lerp(0.8, 0.5, s), t + lerp(0.25, 0.012, s), lerp(1.2, 0.15, s));
    amp.gain.setTargetAtTime(0, off, rel);
    const end = off + Math.max(0.3, rel * 6);
    for (const o of [o1, o2]) { o.start(t); o.stop(end); }
    const v = registry.add({ src: [o1, o2], all: [o1, o2, lpf, amp], start: t, end, tag, out: amp });
    const x = { v, midi, end: off + rel * 4, rec: log ? [t, off + rel * 4] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  return {
    note,
    /** played almost together (strum ≤ 12 ms), the top voice +8 % */
    chord(t, midis, vel, dur, strum = 0.005, relTau = 0.08) {
      const n = midis.length;
      midis.forEach((m, k) => { const dt = n > 1 ? (k * strum) / (n - 1) : 0; note(t + dt, m, k === n - 1 ? Math.min(1, vel * 1.08) : vel, dur - dt, relTau); });
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); },
  };
}

/* ---------------- synth bass: saw + square → resonant LP with a snappy envelope (mono, persistent) ---------------- */
/** p = plan.synth.bass: { sq (square level), q, sus (sustain cutoff × f), env, dec (filter τ) }. slide = portamento τ 30 ms. */
export function createSynthBass(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const saw = osc(ac, 'sawtooth', 55), sq = osc(ac, 'square', 55), sqG = gain(ac, p.sq), lpf = filter(ac, 'lowpass', 300, p.q), amp = gain(ac, 0);
  saw.connect(lpf); sq.connect(sqG); sqG.connect(lpf); lpf.connect(amp); amp.connect(out);
  const P = persist(registry, tag, [saw, sq], [saw, sq, sqG, lpf, amp], t0);
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi);
      for (const prm of [saw.frequency, sq.frequency, lpf.frequency, amp.gain]) hold(prm, t);
      for (const o of [saw, sq]) { if (slide) o.frequency.setTargetAtTime(f, t, 0.03); else o.frequency.setValueAtTime(f, t); }
      // filter: sustain max(220, sus·f) (the 2nd-4th harmonics carry it on phone speakers), spike ×(2.2 + 2.8v)·env ≤ 2.6 kHz
      const fs = Math.max(220, p.sus * f), fp = Math.min(2600, fs * (2.2 + 2.8 * vel) * p.env);
      lpf.frequency.setTargetAtTime(fp, t, 0.002); lpf.frequency.setTargetAtTime(fs, t + 0.008, p.dec);
      const A = 0.18 * vel * SYNTH_LEVELS.bass;                     // attack τ 3 ms, to .7 (τ 300 ms), release τ 30 ms
      amp.gain.setTargetAtTime(A, t, 0.003); amp.gain.setTargetAtTime(0.7 * A, t + 0.03, 0.3);
      amp.gain.setTargetAtTime(0, t + Math.max(0.06, dur), 0.03);
    },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- lead: 'square' (square + saw .5 → LP, vibrato, portamento) or 'glass' (FM 1:3.5) + dotted-8th delay ---------------- */
/** lead = plan.lead ({ voice, pan }), p = plan.synth.lead ({ vib (¢), glide (τ s) }); stepDur sets the delay (3 sixteenths). */
export function createSynthLead(ac, out, send, lead, p, { registry, tag, stepDur, t0 = ac.currentTime }) {
  const nodes = [], src = [], N = x => (nodes.push(x), x);
  const amp = N(gain(ac, 0)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.35));
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 2500, 0.5)), fb = N(gain(ac, 0.32)), wet = N(gain(ac, 0.35));
  dly.delayTime.value = Math.min(1.9, 3 * stepDur);
  amp.connect(pan); amp.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  pan.connect(out); pan.connect(sendG); sendG.connect(send);
  const L = SYNTH_LEVELS.lead;
  let play;
  if (lead.voice === 'glass') {
    // FM glass: carrier f, modulator 3.5f (inharmonic shimmer), index 2·min(1, (523/f)^.7) → 0.25 of it (τ 0.2 s);
    // amplitude 0.38v, bell decay τ 1 s, release τ 0.3 s; LP 4.5 kHz keeps the attack's sidebands off the top
    const car = N(osc(ac, 'sine', 440)), mod = N(osc(ac, 'sine', 1540)), modG = N(gain(ac, 0)), lp = N(filter(ac, 'lowpass', 4500, 0.5));
    mod.connect(modG); modG.connect(car.frequency); car.connect(lp); lp.connect(amp);
    src.push(car, mod);
    play = (t, f, vel, dur) => {
      for (const prm of [car.frequency, mod.frequency, modG.gain, amp.gain]) hold(prm, t);
      const I = 2 * Math.min(1, Math.pow(523 / f, 0.7)) * 3.5 * f;
      car.frequency.setValueAtTime(f, t); mod.frequency.setValueAtTime(3.5 * f, t);
      modG.gain.setTargetAtTime(I, t, 0.002); modG.gain.setTargetAtTime(0.25 * I, t + 0.01, 0.2);
      amp.gain.setTargetAtTime(0.38 * vel * L, t, 0.002); amp.gain.setTargetAtTime(0, t + 0.01, 1);
      amp.gain.setTargetAtTime(0, t + dur, 0.3);
    };
  } else {
    // square + saw (.5, +6 ¢) → LP max(1.8k, 2.2f) Q 1.1 with a small spike (×1.6, τ 150 ms); amplitude 0.4v (attack τ 8 ms,
    // to .8 over τ 0.4 s, release τ 90 ms); vibrato 5.3 Hz ±p.vib ¢ from 220 ms; a legato step of ≤ 5 semitones glides
    // (portamento τ p.glide, no filter spike), a bigger leap is played fresh
    const sq = N(osc(ac, 'square', 440)), saw = N(osc(ac, 'sawtooth', 440)), sawG = N(gain(ac, 0.5)), lp = N(filter(ac, 'lowpass', 2000, 1.1));
    const vib = N(osc(ac, 'sine', 5.3)), vibG = N(gain(ac, 0));
    saw.detune.value = 6;
    sq.connect(lp); saw.connect(sawG); sawG.connect(lp); lp.connect(amp);
    vib.connect(vibG); vibG.connect(sq.detune); vibG.connect(saw.detune);
    src.push(sq, saw, vib);
    let lastF = 0;
    play = (t, f, vel, dur, legato) => {
      const glide = legato && Math.abs(12 * Math.log2(f / (lastF || f))) <= 5;
      lastF = f;
      for (const prm of [sq.frequency, saw.frequency, lp.frequency, amp.gain, vibG.gain]) hold(prm, t);
      for (const o of [sq, saw]) { if (glide) o.frequency.setTargetAtTime(f, t, p.glide); else o.frequency.setValueAtTime(f, t); }
      const fc = Math.max(1800, 2.2 * f);
      if (glide) lp.frequency.setTargetAtTime(fc, t, 0.05);
      else { lp.frequency.setTargetAtTime(1.6 * fc, t, 0.004); lp.frequency.setTargetAtTime(fc, t + 0.01, 0.15); }
      const A = 0.4 * vel * L;
      amp.gain.setTargetAtTime(A, t, 0.008); amp.gain.setTargetAtTime(0.8 * A, t + 0.05, 0.4);
      amp.gain.setTargetAtTime(0, t + dur, 0.09);
      vibG.gain.setTargetAtTime(0, t, 0.02);
      if (dur > 0.3) vibG.gain.setTargetAtTime(p.vib, t + 0.22, 0.15);
    };
  }
  const P = persist(registry, tag, src, nodes, t0);
  return {
    note(t, midi, vel, dur, glide = false) { play(t, mtof(midi), vel, Math.max(0.06, dur), glide); },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- kit: createKit (tuned by the plan) plus a tom that tunes down the fill ---------------- */
/** The tom reads its step from the event: steps 10…15 run 240 Hz → 120 Hz (−2.4 semitones a step), each hit sweeping to
 *  .62 of it (τ 45 ms), 0.5v, decay τ 0.2 s, panned +.35 (high) → −.35 (floor), reverb .2. Clap and shaker stay createPerc's. */
export function createCityKit(ac, out, send, kit, { registry, tag, stepDur, t0 = ac.currentTime }) {
  const base = createKit(ac, out, send, kit, { registry, tag, t0 });
  const o = osc(ac, 'sine', 150), a = gain(ac, 0), pn = panner(ac, 0), sg = gain(ac, 0.2);
  o.connect(a); a.connect(pn); pn.connect(out); pn.connect(sg); sg.connect(send);
  const P = persist(registry, tag, [o], [o, a, pn, sg], t0);
  return {
    ...base,
    tom(t, v, e) {
      const k = e ? clamp(Math.round(e.t / stepDur) % 16, 10, 15) - 10 : 0, f0 = 240 * Math.pow(2, -k / 5);
      o.frequency.setValueAtTime(f0, t); o.frequency.setTargetAtTime(0.62 * f0, t, 0.045);
      pn.pan.setValueAtTime(0.35 - 0.14 * k, t);
      a.gain.setTargetAtTime(0.5 * v * LEVELS.kit * SYNTH_LEVELS.tom, t, 0.001); a.gain.setTargetAtTime(0, t + 0.005, 0.2);
    },
    dispose(t) { base.dispose(t); P.dispose(t); },
  };
}
